import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, Info, X, Mail, Search, Tv, Film, Music, Users, Copy, Share2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { showUpgradeExternally } from "../components/UpgradePrompt";
import { askForPremium } from "../lib/askPremium";
import { useDjLook, LOOKS, MINE_PARTS } from "../lib/djLook";
import "../components/djbooth.css";

// The DJ Booth (Premium). This page is the CONTROLLER, made for a phone: four decks, a crossfader, a tempo and a volume slider on each
// deck and the song list. A small phone shows two decks at a time (a switch picks Decks 1 & 2 or Decks 3 & 4); a tablet or an unfolded
// foldable (640 px wide or more) shows all four. Decks 1 and 3 are the crossfader's left side, decks 2 and 4 its right side.
// The picture and the sound come out of the Screen page (pages/DjScreen.js) on a TV. A short code on the TV links the two.
// Everything goes through the server (backend/dj.py): this page sends commands and asks "has anything changed?".
// The Library also lists the videos and music files the Screen page holds from its own device ("On the screen"); they load on a deck like a song.
// On a friend's page (pages/DjJoin.js) it also has "From my phone": the friend's own files, which travel to the screen after they are loaded.
// Looks: a Look pull-down (Steel, Wood, Mint, Cherry, Space age, Gold, Quartz, or "My colors" with the person's own Base, Knobs,
// Switches and Lights) repaints the controller; lib/djLook.js holds the colors, components/djbooth.css paints with them.

const clamp01 = (x) => Math.max(0, Math.min(1, x));
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// the left side of the crossfader (decks 1 and 3) is cool colors, the right side (decks 2 and 4) warm
export const ACCENT = [
  { name: "Deck 1", hex: "#22d3ee" },
  { name: "Deck 2", hex: "#fbbf24" },
  { name: "Deck 3", hex: "#34d399" },
  { name: "Deck 4", hex: "#fb7185" },
];
// the light of one deck: its own color, unless the person chose one Lights color for everything ("My colors" sets --dj-lights)
export const lit = (i) => ({ "--lit": `var(--dj-lights, ${ACCENT[i % ACCENT.length].hex})` });
const WIDE_PX = 640;                  // from this width (a tablet, an unfolded foldable) all four decks show at once

export const mmss = (s) => {
  const t = Math.max(0, Math.round(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

// What went wrong, in words. The site already shows its own box for "Premium only" and "18+ check needed".
export const say = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (d === "premium_required" || d === "age_required" || d === "no_booth") return;
  toast.error(typeof d === "string" ? d : fallback);
};

// Of two copies of the booth, keep the newer one (answers can arrive out of order).
export const newer = (prev, next) => {
  if (!next) return next;
  if (prev && prev.code === next.code && prev.v > next.v) return prev;
  return next;
};

// Sends at most one request every `ms` while a slider is dragged, and always the last value.
function useThrottle(fn, ms) {
  const s = useRef({ last: 0, timer: null, body: null });
  useEffect(() => { const cur = s.current; return () => clearTimeout(cur.timer); }, []);
  return useCallback((body) => {
    const c = s.current;
    c.body = body;
    const wait = ms - (Date.now() - c.last);
    if (wait <= 0) {
      clearTimeout(c.timer); c.timer = null;
      c.last = Date.now(); c.body = null;
      fn(body);
    } else if (!c.timer) {
      c.timer = setTimeout(() => {
        c.timer = null; c.last = Date.now();
        const b = c.body; c.body = null;
        if (b) fn(b);
      }, wait);
    }
  }, [fn, ms]);
}

// true when the window is at least `px` wide (follows the window as it changes, e.g. a foldable being opened)
export function useWide(px) {
  const query = `(min-width: ${px}px)`;
  const [wide, setWide] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return wide;
}

// A value the person just changed stays on show for a moment, then the server's value takes over again.
function useHeld(server, holdMs = 1200) {
  const [local, setLocal] = useState(null);
  const timer = useRef(null);
  const set = useCallback((v) => {
    setLocal(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setLocal(null), holdMs);
  }, [holdMs]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [local !== null ? local : server, set];
}

// ---------------------------------------------------------------------------
// The Look pull-down and "My colors" (used on the booth, in the manual and on the page a guest or free member sees)
// ---------------------------------------------------------------------------
export function LookPicker({ look, tid }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`${tid}-look`}>
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        Look
        <select value={look.look} onChange={(e) => look.setLook(e.target.value)} data-testid={`${tid}-look-select`}
          className="bg-zinc-900 border border-white/15 rounded-lg px-2 py-2 text-white">
          {LOOKS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </label>
      {look.look === "mine" && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid={`${tid}-mine`}>
          {MINE_PARTS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm text-zinc-300">
              {label}
              <input type="color" value={look.mine[k]} onChange={(e) => look.setMine({ [k]: e.target.value })}
                aria-label={`${label} color`} data-testid={`${tid}-mine-${k}`}
                className="w-9 h-9 rounded-md bg-transparent border border-white/20 p-0.5 cursor-pointer" />
            </label>
          ))}
          <button type="button" onClick={look.resetMine} data-testid={`${tid}-mine-reset`}
            className="text-xs px-2.5 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:bg-white/10">Reset colors</button>
        </div>
      )}
    </div>
  );
}

// A picture of the controller that is not connected to anything: made-up songs, nothing moves or sends. It is painted by the same
// look as the real one, so a guest or a free member can try the looks before going Premium. `compact` = two decks only (for the manual box).
const SAMPLE_DECKS = [
  { title: "Sample Song One", pct: 42, playing: true, time: "1:24", left: "-1:56" },
  { title: "Sample Song Two", pct: 0, playing: false, time: "0:00", left: "-3:10" },
  { title: "Sample Song Three", pct: 0, playing: false, time: "0:00", left: "-2:48" },
  { title: "Sample Song Four", pct: 0, playing: false, time: "0:00", left: "-3:31" },
];
function Sample({ look, compact }) {
  const noop = () => {};
  return (
    <div className="dj-console pointer-events-none select-none" style={look.vars} aria-hidden="true" data-testid="dj-sample" data-look={look.look}>
      <div className="flex items-center justify-between">
        <span className="text-sm flex items-center gap-2 dj-ok"><span className="w-2.5 h-2.5 rounded-full bg-current" />Screen connected (SAMPLE)</span>
        <span className="dj-sw is-flat text-sm px-3 py-1.5 rounded-full">End booth</span>
      </div>
      <div className={`grid gap-3 mt-3 ${compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`}>
        {SAMPLE_DECKS.map((d, i) => (
          <div key={i} className={i > 1 ? (compact ? "hidden" : "hidden sm:block") : ""}>
            <div className={`dj-card rounded-2xl p-3 ${d.playing ? "is-live" : ""}`} style={lit(i)}>
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 mb-2">
                <span className="flex items-center gap-1.5 text-xs font-bold tracking-widest whitespace-nowrap dj-strong">
                  <span className={`dj-led shrink-0 ${d.playing ? "" : "is-off"}`} />{ACCENT[i].name.toUpperCase()}
                  <span className="font-normal tracking-normal text-zinc-500">{i % 2 === 0 ? "left" : "right"}</span>
                </span>
                <span className="dj-sw is-flat text-xs px-2.5 py-1 rounded-full shrink-0">Eject</span>
              </div>
              <div className="font-semibold text-sm leading-tight dj-strong">{d.title}</div>
              <div className="text-xs text-zinc-500 truncate">Sample artist</div>
              <div className="py-2"><div className="dj-prog h-3 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${d.pct}%` }} /></div></div>
              <div className="flex justify-between text-[11px] text-zinc-500 tabular-nums"><span>{d.time}</span><span>{d.left}</span></div>
              <div className={`dj-sw is-deck ${d.playing ? "is-on" : ""} mt-2 w-full h-12 rounded-xl flex items-center justify-center gap-2`}>
                {d.playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}<span>{d.playing ? "Pause" : "Play"}</span>
              </div>
              <div className="mt-2 text-xs text-zinc-400">Tempo</div>
              <input type="range" className="dj-range is-plain h-8" min="85" max="115" value="100" onChange={noop} disabled tabIndex={-1} />
              <div className="text-xs text-zinc-400">Volume</div>
              <input type="range" className="dj-range h-8" min="0" max="100" value="100" onChange={noop} disabled tabIndex={-1} style={{ "--p": "100%" }} />
            </div>
          </div>
        ))}
      </div>
      <div className="dj-card rounded-2xl p-4 mt-3">
        <div className="flex justify-between text-xs font-bold tracking-widest mb-1 dj-strong"><span>DECKS 1 + 3</span><span className="text-zinc-500 font-normal tracking-normal">Crossfader</span><span>DECKS 2 + 4</span></div>
        <input type="range" className="dj-range is-plain is-fader h-12" min="0" max="1000" value="380" onChange={noop} disabled tabIndex={-1} />
        <div className="mt-1"><span className="dj-sw px-4 py-2.5 rounded-full inline-block">Fade to Deck 2</span></div>
        <div className="mt-3 text-xs text-zinc-400">Master</div>
        <input type="range" className="dj-range h-8" min="0" max="100" value="90" onChange={noop} disabled tabIndex={-1} style={{ "--p": "90%" }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The manual (the "i" box)
// ---------------------------------------------------------------------------
function ManualBox({ premium, user, onClose, look }) {
  const [manual, setManual] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    let dead = false;
    api.get("/dj/manual").then((r) => { if (!dead) setManual(r.data); }).catch(() => { if (!dead) setManual(false); });
    return () => { dead = true; };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const email = async () => {
    if (!user) {                       // a guest creates a free login first: that is how we know it is a person
      showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login", ctaPath: "/register",
        body: "Create a free login to get this manual by email. It goes to the email address on your login.",
      });
      return;
    }
    setSending(true);
    try {
      await api.post("/dj/manual/email");
      toast.success("Sent. Check your inbox (and your spam folder) for \"Your Dan The Man TV DJ booth manual\".");
    } catch (e) { say(e, "The email could not be sent just now."); }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-6" onClick={onClose} data-testid="dj-manual">
      <div role="dialog" aria-modal="true" aria-label="DJ Booth manual" onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/15 bg-zinc-950 p-5 sm:p-7">
        <button type="button" onClick={onClose} aria-label="Close" data-testid="dj-manual-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        <h2 className="font-display text-2xl font-extrabold pr-10">{manual ? manual.title : "DJ Booth: how it works"}</h2>
        {manual === null && <p className="text-zinc-500 mt-4">Loading...</p>}
        {manual === false && <p className="text-amber-300 mt-4">The manual could not be loaded. Try again in a moment.</p>}
        {manual && (
          <>
            <p className="text-zinc-400 mt-2">{manual.intro}</p>
            <section className="mt-5" data-testid="dj-manual-sample">
              <h3 className="font-semibold text-lg mb-1">What it looks like</h3>
              <p className="text-zinc-400 text-[15px] mb-3">This is a sample, with made-up songs. Try the looks below. {premium ? "Your look is kept on this device." : "Your look is kept on this device, so it is waiting for you when you go Premium."}</p>
              <LookPicker look={look} tid="dj-manual" />
              <div className="mt-3"><Sample look={look} compact /></div>
            </section>
            {manual.sections.map((sec) => (
              <section key={sec.title} className="mt-5">
                <h3 className="font-semibold text-lg mb-1">{sec.title}</h3>
                <ol className="list-decimal pl-5 space-y-1.5 text-zinc-300 text-[15px]">
                  {sec.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
              </section>
            ))}
            <button type="button" onClick={email} disabled={sending} data-testid="dj-manual-email"
              className="mt-6 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 flex items-center gap-2 disabled:opacity-50">
              <Mail className="w-4 h-4" /> {sending ? "Sending..." : "Email me this manual"}
            </button>
            {!user && <p className="text-zinc-500 text-[13px] mt-2" data-testid="dj-manual-email-note">A free login is needed to get it by email.</p>}
          </>
        )}
      </div>
    </div>
  );
}

// A slider with a groove and a knob. kind: "fill" (the groove lights up to the knob), "plain" (just a groove), "fader" (plain, with a square cap)
function Slider({ value, min, max, step = 1, onChange, label, testid, kind = "fill" }) {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <input type="range" min={min} max={max} step={step} value={value} onChange={onChange} aria-label={label} data-testid={testid}
      className={`dj-range ${kind === "fader" ? "is-plain is-fader h-12" : kind === "plain" ? "is-plain h-8" : "h-8"}`}
      style={kind === "fill" ? { "--p": `${pct}%` } : undefined} />
  );
}

// ---------------------------------------------------------------------------
// One deck
// ---------------------------------------------------------------------------
export function Deck({ i, deck, send, holder }) {
  const a = ACCENT[i % ACCENT.length];
  const load = deck.load;
  const rep = deck.rep;
  const len = rep && rep.d ? rep.d : 0;
  const t = rep ? rep.t : 0;
  const ended = !!(rep && rep.e);
  const playing = deck.play && !ended;
  const pct = len ? Math.min(100, (t / len) * 100) : 0;
  const pushRate = useThrottle(send, 150);
  const pushLevel = useThrottle(send, 150);
  const [rate, setRate] = useHeld(deck.rate, 1500);
  const [level, setLevel] = useHeld(deck.level === undefined ? 1 : deck.level, 1500);

  const sending = !!(load && load.phone && load.state !== "ready");        // a friend's file that is not (all) on the screen yet
  const press = () => {
    if (!load || sending) return;
    if (ended) send({ deck: i, seek: 0, play: true });
    else send({ deck: i, play: !deck.play });
  };
  const jump = (e) => {
    if (!load || !len) return;
    const r = e.currentTarget.getBoundingClientRect();
    send({ deck: i, seek: clamp01((e.clientX - r.left) / r.width) });
  };
  const onRate = (e) => {
    const v = Number(e.target.value) / 100;
    setRate(v);
    pushRate({ deck: i, rate: v });
  };
  const pctText = `${rate >= 1 ? "+" : "-"}${Math.abs((rate - 1) * 100).toFixed(1)}%`;

  return (
    <div className={`dj-card rounded-2xl p-3 ${playing ? "is-live" : ""}`} style={lit(i)} data-testid={`dj-deck-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 mb-2">
        <span className="flex items-center gap-1.5 text-xs font-bold tracking-widest whitespace-nowrap dj-strong">
          <span className={`dj-led shrink-0 ${playing ? "" : "is-off"}`} />
          {a.name.toUpperCase()}
          <span className="font-normal tracking-normal text-zinc-500">{i % 2 === 0 ? "left" : "right"}</span>
          {holder && <span className="font-semibold tracking-normal dj-ok" data-testid={`dj-holder-${i}`}>· {holder}</span>}
        </span>
        <button type="button" onClick={() => send({ deck: i, eject: true })} disabled={!load} data-testid={`dj-eject-${i}`}
          className="dj-sw is-flat text-xs px-2.5 py-1 rounded-full shrink-0">Eject</button>
      </div>
      <div className="aspect-square rounded-xl overflow-hidden bg-black/40 border border-black/40 flex items-center justify-center mb-2">
        {load && load.cover ? <img src={load.cover} alt="" className="w-full h-full object-cover" />
          : load && load.local ? <span className="text-slate-400 text-sm px-2 text-center flex flex-col items-center gap-1"><Film className="w-8 h-8" />My video</span>
          : <span className="text-slate-500 text-sm px-2 text-center">{load ? "No cover" : "Empty"}</span>}
      </div>
      <div className="min-h-[2.75rem]">
        <div className="font-semibold text-sm leading-tight line-clamp-2 dj-strong" data-testid={`dj-deck-title-${i}`}>{load ? load.title : "Load a song below"}</div>
        <div className="text-xs text-zinc-500 truncate">{load ? load.artist : ""}</div>
        {load && load.who && <div className="text-xs dj-ok truncate" data-testid={`dj-loaded-by-${i}`}>Loaded by {load.who}</div>}
        {load && load.phone && (
          <div className={`text-xs truncate ${load.state === "ready" ? "dj-ok" : "dj-warn"}`} data-testid={`dj-state-${i}`}>
            {load.state === "ready" ? "Ready on the TV" : load.state === "failed" ? "Did not reach the TV. Load it again." : "On its way to the TV..."}
          </div>
        )}
      </div>
      <div onClick={jump} role="presentation" data-testid={`dj-progress-${i}`} className="mt-1 py-2 cursor-pointer">
        <div className="dj-prog h-3 rounded-full overflow-hidden">
          <div className="h-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex justify-between text-[11px] text-zinc-500 mt-1 tabular-nums">
        <span>{load ? mmss(t) : "0:00"}</span>
        <span>{ended ? "Ended" : len ? `-${mmss(len - t)}` : ""}</span>
      </div>
      <button type="button" onClick={press} disabled={!load || sending} data-testid={`dj-play-${i}`}
        aria-label={playing ? `Pause ${a.name}` : `Play ${a.name}`}
        className={`dj-sw is-deck ${playing ? "is-on" : ""} mt-2 w-full h-14 rounded-xl flex items-center justify-center gap-2`}>
        {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
        <span>{sending ? (load.state === "failed" ? "Not sent" : "On its way") : playing ? "Pause" : ended ? "Again" : "Play"}</span>
      </button>
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
          <span>Tempo</span>
          <button type="button" onClick={() => { setRate(1); send({ deck: i, rate: 1 }); }} data-testid={`dj-tempo-reset-${i}`}
            className="dj-sw is-flat tabular-nums px-2 py-0.5 rounded-full">{pctText}</button>
        </div>
        <Slider kind="plain" min={85} max={115} step={0.5} value={Math.round(rate * 200) / 2} onChange={onRate}
          label={`${a.name} tempo`} testid={`dj-tempo-${i}`} />
      </div>
      <div className="mt-1">
        <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
          <span>Volume</span>
          <span className="tabular-nums" data-testid={`dj-level-text-${i}`}>{Math.round(level * 100)}%</span>
        </div>
        <Slider min={0} max={100} step={1} value={Math.round(level * 100)}
          onChange={(e) => { const v = Number(e.target.value) / 100; setLevel(v); pushLevel({ deck: i, level: v }); }}
          label={`${a.name} volume`} testid={`dj-level-${i}`} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The crossfader, the fade button and the master volume
// ---------------------------------------------------------------------------
function Mixer({ booth, send, wide, pair }) {
  const [gx, setGx] = useState(null);
  const gRef = useRef(null);
  const xNow = useRef(0);
  const g = booth.glide;
  const gN = g ? g.n : null;
  gRef.current = g;
  const [x, setX] = useHeld(gx !== null ? gx : booth.xfade, 900);
  xNow.current = x;
  const [master, setMaster] = useHeld(booth.master, 900);
  const pushX = useThrottle(send, 110);
  const pushMaster = useThrottle(send, 150);
  const [secs, setSecs] = useState(() => {
    try { const s = Number(localStorage.getItem("dtm_dj_fade")); return [4, 8, 12, 20].includes(s) ? s : 8; } catch { return 8; }
  });

  // a fade to the other deck: the screen runs it, and this slider follows along on its own
  useEffect(() => {
    const cur = gRef.current;
    if (!gN || !cur) { setGx(null); return undefined; }
    const from = xNow.current;
    const to = cur.to;
    const ms = Math.max(1000, cur.secs * 1000);
    const t0 = performance.now();
    let raf = 0;
    const tick = (now) => {
      const p = clamp01((now - t0) / ms);
      setGx(from + (to - from) * (p * p * (3 - 2 * p)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gN]);

  const onX = (e) => {
    const v = Number(e.target.value) / 1000;
    setGx(null);
    setX(v);
    pushX({ xfade: v });
  };
  const toTwo = x < 0.5;                           // true: the next fade goes to the right side
  // on a small phone the fade names the deck of the pair on show; with all four on show the server picks the waiting deck
  const hintDeck = pair * 2 + (toTwo ? 1 : 0);
  const fade = () => send({ glide: { to: toTwo ? 1 : 0, secs, ...(wide ? {} : { deck: hintDeck }) } });
  const fadeText = wide ? (toTwo ? "Fade to Decks 2 + 4" : "Fade to Decks 1 + 3") : `Fade to Deck ${hintDeck + 1}`;
  const pickSecs = (e) => {
    const s = Number(e.target.value);
    setSecs(s);
    try { localStorage.setItem("dtm_dj_fade", String(s)); } catch { /* ignore */ }
  };

  return (
    <div className="dj-card rounded-2xl p-4 mt-3" data-testid="dj-mixer">
      <div className="flex justify-between text-xs font-bold tracking-widest mb-1 dj-strong">
        <span>DECKS 1 + 3</span>
        <span className="text-zinc-500 font-normal tracking-normal">Crossfader</span>
        <span>DECKS 2 + 4</span>
      </div>
      <Slider kind="fader" min={0} max={1000} value={Math.round(clamp01(x) * 1000)} onChange={onX} label="Crossfader" testid="dj-xfade" />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button type="button" onClick={fade} data-testid="dj-fade" className="dj-sw px-4 py-2.5 rounded-full">
          {gN ? "Fading..." : fadeText}
        </button>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          over
          <select value={secs} onChange={pickSecs} data-testid="dj-fade-secs" className="dj-field rounded-lg px-2 py-2">
            {[4, 8, 12, 20].map((s) => <option key={s} value={s}>{s} seconds</option>)}
          </select>
        </label>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>Master</span><span className="tabular-nums">{Math.round(master * 100)}%</span></div>
        <Slider min={0} max={100} value={Math.round(master * 100)}
          onChange={(e) => { const v = Number(e.target.value) / 100; setMaster(v); pushMaster({ master: v }); }}
          label="Master volume" testid="dj-master" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The song list
// ---------------------------------------------------------------------------
// `mine` (only on a friend's page) = { fid, files: [{ k, t, a, size, mime }], pick(), remove(k), rights, setRights(bool) }: the files the friend chose
// on their own phone, and whether they ticked "I made this, or I have the right to play it here" (the server refuses a file without it).
export function Library({ booth, send, decksShown, fetchFiles, favorites = true, mine = null }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [tab, setTab] = useState("all");
  const [limit, setLimit] = useState(40);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);              // the videos the screen holds from its own device

  useEffect(() => {
    const t = setTimeout(() => { setDq(q.trim()); setLimit(40); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // the screen's own videos: asked for when that tab is open, and again whenever the screen changes its list
  useEffect(() => {
    if (tab !== "screen") return undefined;
    let dead = false;
    (fetchFiles ? fetchFiles() : api.get("/dj/files")).then((r) => { if (!dead) { setFiles(Array.isArray(r.data.files) ? r.data.files : []); setErr(""); } })
      .catch(() => { if (!dead) setErr("Could not load the videos on the screen."); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, booth.fv]);

  useEffect(() => {
    if (tab === "screen" || tab === "phone") { setBusy(false); return undefined; }
    let dead = false;
    setBusy(true);
    const req = tab === "fav" ? api.get("/favorites") : api.get("/videos", { params: { q: dq || undefined, limit } });
    req.then((r) => { if (!dead) { setRows(Array.isArray(r.data) ? r.data : []); setErr(""); } })
      .catch(() => { if (!dead) setErr("Could not load the songs."); })
      .finally(() => { if (!dead) setBusy(false); });
    return () => { dead = true; };
  }, [tab, dq, limit]);

  const shown = tab === "phone"
    ? ((mine && mine.files) || []).filter((f) => !dq || f.t.toLowerCase().includes(dq.toLowerCase())).map((f) => ({
      id: `phone:${mine.fid}-${f.k}`, title: f.t, artist: "From my phone", local: true, music: !!f.a, mineKey: f.k,
      extra: { file: { k: f.k, t: f.t, a: !!f.a, size: f.size, mime: f.mime, ok: !!mine.rights } },
    }))
    : tab === "screen"
    ? files.filter((f) => !dq || f.t.toLowerCase().includes(dq.toLowerCase())).map((f) => ({ id: `local:${f.k}`, title: f.t, artist: "On the screen", local: true, music: !!f.a }))
    : rows
      .filter((v) => !v.age_locked)
      .filter((v) => tab !== "fav" || !dq || `${v.title} ${v.artist}`.toLowerCase().includes(dq.toLowerCase()));
  const onDeck = (id) => booth.decks.map((d, i) => (d.load && d.load.vid === id ? i : -1)).filter((i) => i >= 0);

  return (
    <div className="mt-6" data-testid="dj-library">
      <div className="flex gap-2 mb-3">
        {[["all", "All songs"], ...(favorites ? [["fav", "Favorites"]] : []), ["screen", `On the screen${booth.nfiles ? ` (${booth.nfiles})` : ""}`],
          ...(mine ? [["phone", `From my phone${mine.files.length ? ` (${mine.files.length})` : ""}`]] : [])].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`dj-tab-${k}`} aria-pressed={tab === k}
            className="dj-sw px-4 py-1.5 rounded-full text-sm">{label}</button>
        ))}
      </div>
      <label className="relative block mb-3">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "screen" || tab === "phone" ? "Search your files" : "Search for a song"} data-testid="dj-search"
          className="dj-field w-full rounded-full pl-9 pr-4 py-2.5 text-base" />
      </label>
      {err && <p className="dj-warn text-sm mb-2">{err}</p>}
      {tab === "phone" && mine && (
        <div className="mb-3" data-testid="dj-phone-add">
          <label className="flex items-start gap-2 mb-3 text-sm text-zinc-300">
            <input type="checkbox" checked={!!mine.rights} onChange={(e) => mine.setRights(e.target.checked)} data-testid="dj-phone-rights" className="mt-1" />
            <span>I made this, or I have the right to play it here. I will not send anything I do not have the right to play.</span>
          </label>
          <button type="button" onClick={mine.pick} disabled={!mine.rights} data-testid="dj-phone-pick" className="dj-sw px-4 py-2 rounded-full text-sm">Choose videos or music from my phone</button>
          <p className="text-xs text-zinc-500 mt-2">Your file goes straight to the TV and is not saved anywhere. Press a deck number, then wait until the deck says Ready before you play it. Up to 300 MB each.</p>
        </div>
      )}
      <ul className="space-y-2">
        {shown.map((v) => {
          const here = onDeck(v.id);
          return (
            <li key={v.id} data-testid={`dj-row-${v.id}`} className="dj-card flex items-center gap-3 rounded-xl p-2">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/40 shrink-0">
                {v.cover_url ? <img src={v.cover_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : v.local ? <span className="w-full h-full flex items-center justify-center text-slate-400">{v.music ? <Music className="w-5 h-5" /> : <Film className="w-5 h-5" />}</span> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate dj-strong">{v.title}</div>
                <div className="text-xs text-zinc-500 truncate">{v.artist}{here.length ? ` · on Deck ${here.map((h) => h + 1).join(" and ")}` : ""}</div>
              </div>
              {v.mineKey && (
                <button type="button" onClick={() => mine.remove(v.mineKey)} data-testid={`dj-phone-remove-${v.mineKey}`} aria-label={`Remove ${v.title} from my list`}
                  className="dj-sw is-flat text-xs px-2.5 py-1 rounded-full shrink-0">Remove</button>
              )}
              {decksShown.map((i) => (
                <button key={i} type="button" onClick={() => send({ deck: i, load: v.id, ...(v.extra || {}) })} disabled={!!v.mineKey && !mine.rights} data-testid={`dj-load-${v.id}-${i}`}
                  aria-label={`Load ${v.title} on ${ACCENT[i].name}`} style={lit(i)}
                  className="dj-sw is-deck w-11 h-11 rounded-full font-extrabold text-lg shrink-0">{i + 1}</button>
              ))}
            </li>
          );
        })}
      </ul>
      {busy && <p className="text-zinc-500 text-sm mt-3">Loading...</p>}
      {!busy && !shown.length && !err && (
        <p className="text-zinc-500 text-sm mt-3" data-testid="dj-empty">
          {tab === "fav" ? "No favorites to show."
            : tab === "phone" ? (mine && mine.files.length ? "No file matches your search." : "Nothing here yet. Press \"Choose videos or music from my phone\".")
            : tab === "screen" ? (booth.nfiles ? "No file matches your search." : "Nothing on the screen yet. On the device that shows the screen, press \"Add videos or music from this device\".")
            : "No songs found."}
        </p>
      )}
      {tab === "all" && !busy && rows.length >= limit && (
        <button type="button" onClick={() => setLimit(limit + 40)} data-testid="dj-more"
          className="dj-sw mt-4 px-4 py-2 rounded-full">Show more songs</button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linking the phone to a screen
// ---------------------------------------------------------------------------
function PairCard({ onPaired }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault();
    if (code.length < 5) { setErr("The code has 5 letters or numbers."); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/dj/pair", { code });
      onPaired(r.data.booth);
    } catch (er) {
      const d = er?.response?.data?.detail;
      if (d !== "premium_required") setErr(typeof d === "string" ? d : "That did not work. Try again.");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={go} className="dj-card rounded-2xl p-5 max-w-xl" data-testid="dj-pair">
      <div className="flex items-center gap-2 font-semibold text-lg mb-2 dj-strong"><Tv className="w-5 h-5" /> Connect a screen</div>
      <ol className="list-decimal pl-5 space-y-1 text-zinc-300 text-[15px] mb-4">
        <li>On the TV's web browser, go to <b className="dj-strong">{typeof window !== "undefined" ? window.location.host : "danthemantv.com"}/dj/screen</b></li>
        <li>Press OK on <b className="dj-strong">Start screen</b>. A code shows up.</li>
        <li>Type the code here.</li>
      </ol>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5))}
          placeholder="CODE" autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Screen code"
          data-testid="dj-code-input"
          className="dj-field w-40 rounded-xl px-4 py-3 text-2xl font-extrabold tracking-[0.25em] text-center" />
        <button type="submit" disabled={busy || code.length < 5} data-testid="dj-connect"
          className="dj-sw px-5 rounded-xl">{busy ? "Connecting..." : "Connect"}</button>
      </div>
      {err && <p className="dj-warn text-sm mt-3" data-testid="dj-pair-error">{err}</p>}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Friends' party: the host's box (the invite link, who is in, who has which deck)
// ---------------------------------------------------------------------------
function PartyCard({ booth, onBooth }) {
  const party = booth.party || { on: false, friends: [], seats: [] };
  const [busy, setBusy] = useState(false);
  const link = party.invite && typeof window !== "undefined" ? `${window.location.origin}/dj/join/${party.invite}` : "";
  const call = async (path, body) => {
    setBusy(true);
    try {
      const r = await api.post(path, body);
      onBooth(r.data.booth);
    } catch (e) { say(e, "That did not go through."); }
    setBusy(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied. Paste it in a text to your friends."); }
    catch { toast.message("Press and hold the link to copy it."); }
  };
  const share = async () => {
    try { await navigator.share({ title: "Join my DJ party", text: "Join my DJ party on Dan The Man TV:", url: link }); }
    catch { /* closed without sharing */ }
  };
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  return (
    <div className="dj-card rounded-2xl p-4 mt-3" data-testid="dj-party">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-semibold dj-strong"><Users className="w-5 h-5" /> Friends' party</span>
        {party.on && (
          <button type="button" onClick={() => call("/dj/party", { on: false })} disabled={busy} data-testid="dj-party-stop"
            className="dj-sw is-flat text-sm px-3 py-1.5 rounded-full">Stop the party</button>
        )}
      </div>
      {!party.on && (
        <>
          <p className="text-sm text-zinc-400 mt-2">Let friends join from their own phones. Each friend takes a free deck and loads songs on it. You keep the crossfader, the Fade and Master.</p>
          <button type="button" onClick={() => call("/dj/party", { on: true })} disabled={busy} data-testid="dj-party-start"
            className="dj-sw px-5 py-2.5 rounded-full mt-3">Invite friends</button>
        </>
      )}
      {party.on && (
        <>
          <p className="text-sm text-zinc-400 mt-2">Send this link to your friends. They need a free login to take a deck.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <input readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Invite link" data-testid="dj-party-link"
              className="dj-field flex-1 min-w-[12rem] rounded-xl px-3 py-2.5 text-sm" />
            <button type="button" onClick={copy} data-testid="dj-party-copy" className="dj-sw px-4 py-2.5 rounded-full flex items-center gap-1.5"><Copy className="w-4 h-4" /> Copy link</button>
            {canShare && <button type="button" onClick={share} data-testid="dj-party-share" className="dj-sw px-4 py-2.5 rounded-full flex items-center gap-1.5"><Share2 className="w-4 h-4" /> Share</button>}
          </div>
          <label className="flex items-start gap-2 mt-3 text-sm text-zinc-300">
            <input type="checkbox" checked={party.files !== false} disabled={busy} data-testid="dj-party-files"
              onChange={(e) => call("/dj/party/options", { files: e.target.checked })} className="mt-1" />
            <span>Friends can play files from their own phones<span className="block text-xs text-zinc-500">Each file goes straight to your screen and is not saved anywhere.</span></span>
          </label>
          <ul className="mt-3 space-y-2" data-testid="dj-party-friends">
            {party.friends.map((f) => (
              <li key={f.id} data-testid={`dj-party-friend-${f.id}`} className="flex flex-wrap items-center gap-2 rounded-xl bg-black/20 px-3 py-2">
                <span className="font-semibold text-sm dj-strong flex-1 min-w-[5rem] truncate">{f.name}</span>
                <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                  Deck
                  <select value={f.deck === null || f.deck === undefined ? "" : String(f.deck)} data-testid={`dj-party-seat-${f.id}`} disabled={busy}
                    onChange={(e) => call("/dj/party/seat", { deck: e.target.value === "" ? (f.deck ?? 0) : Number(e.target.value), friend: e.target.value === "" ? null : f.id })}
                    className="dj-field rounded-lg px-2 py-1.5 text-sm">
                    <option value="">None</option>
                    {booth.decks.map((_, i) => {
                      const other = party.seats[i] && party.seats[i].id !== f.id ? party.seats[i].name : "";
                      return <option key={i} value={String(i)}>{`Deck ${i + 1}${other ? ` (${other} has it)` : ""}`}</option>;
                    })}
                  </select>
                </label>
                <button type="button" onClick={() => call("/dj/party/remove", { friend: f.id })} disabled={busy} data-testid={`dj-party-remove-${f.id}`}
                  className="dj-sw is-flat text-xs px-3 py-1.5 rounded-full">Remove</button>
              </li>
            ))}
            {!party.friends.length && <li className="text-sm text-zinc-500" data-testid="dj-party-empty">Nobody has joined yet.</li>}
          </ul>
          <div className="mt-3">
            <button type="button" onClick={() => call("/dj/party", { on: true, renew: true })} disabled={busy} data-testid="dj-party-renew"
              className="dj-sw is-flat text-xs px-3 py-1.5 rounded-full">Make a new link (the old one stops working)</button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------
export default function DjBooth() {
  const { user, ready } = useAuth();
  const { setPlaying } = usePlayer();
  const premium = !!user && (user.is_premium || user.role === "admin");
  const [booth, setBooth] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [kick, setKick] = useState(0);
  const [manual, setManual] = useState(false);
  const wide = useWide(WIDE_PX);
  const look = useDjLook();
  const [pair, setPair] = useState(0);              // on a small phone: 0 = Decks 1 & 2, 1 = Decks 3 & 4
  const closeManual = useCallback(() => setManual(false), []);

  // The site's own player must not play under this page.
  useEffect(() => { setPlaying(false); }, [setPlaying]);

  useEffect(() => {
    const title = document.title;
    document.title = "DJ Booth | Dan The Man TV";
    return () => { document.title = title; };
  }, []);

  // Listen for changes: the answer comes back the moment something changes (or after about 15 seconds of nothing).
  useEffect(() => {
    if (!premium) return undefined;
    let dead = false;
    let since = -1;
    (async () => {
      while (!dead) {
        try {
          const r = await api.get("/dj/wait", { params: { since }, timeout: 30000 });
          if (dead) return;
          setLoaded(true);
          const b = r.data.booth;
          if (!b) { setBooth(null); return; }
          since = b.v;
          setBooth((prev) => newer(prev, b));
        } catch (e) {
          if (dead) return;
          const s = e?.response?.status;
          if (s === 401 || s === 403) { setLoaded(true); return; }
          await sleep(2000);
        }
      }
    })();
    return () => { dead = true; };
  }, [premium, kick]);

  const send = useCallback(async (body) => {
    try {
      const r = await api.post("/dj/cmd", body);
      setBooth((prev) => newer(prev, r.data.booth));
    } catch (e) {
      if (e?.response?.status === 404 && e?.response?.data?.detail === "no_booth") { setBooth(null); return; }
      say(e, "That did not go through.");
    }
  }, []);

  const paired = (b) => { setBooth(b); setKick((k) => k + 1); };
  const endBooth = async () => {
    try { await api.post("/dj/end"); } catch (e) { say(e, "Could not end the booth."); return; }
    setBooth(null);
  };

  const needPremium = () => {
    if (!user) {
      showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login", ctaPath: "/register",
        body: "Create a free login first, then go Premium to use the DJ Booth: four decks on your phone, with the video on your TV.",
      });
    } else {
      askForPremium("The DJ Booth is a Premium feature", {
        ctaLabel: "Sign up for Premium", ctaPath: "/account?checkout=1",
        body: "Premium members run four decks from their phone, with the video and sound on their TV.",
      });
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="dj-page">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">DJ Booth</h1>
        <button type="button" onClick={() => setManual(true)} aria-label="How the DJ Booth works" data-testid="dj-info"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center"><Info className="w-5 h-5" /></button>
      </div>
      <p className="text-zinc-400 mt-2">Four decks on your phone. The video and the sound come out of your TV.</p>

      {!ready && <p className="text-zinc-500 mt-6">Loading...</p>}

      {ready && !premium && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 mt-5 max-w-2xl" data-testid="dj-locked">
          <p className="text-zinc-300">The DJ Booth is for Premium members. Open the Screen page on a TV, type its code on your phone, load songs on up to four decks, and mix between them with the crossfader. Press the "i" button for the whole manual.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" onClick={needPremium} data-testid="dj-unlock"
              className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-bold hover:bg-cyan-300">{user ? "Sign up for Premium" : "Create a free login"}</button>
            {!user && <Link to="/login" className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/10">Log in</Link>}
          </div>
        </div>
      )}

      {ready && !premium && (
        <div className="mt-6" data-testid="dj-try">
          <h2 className="font-display text-2xl font-extrabold">Try the looks</h2>
          <p className="text-zinc-400 mt-1 mb-3" data-testid="dj-look-note">
            This is a sample with made-up songs. Pick a finish or choose your own colors. Your look is kept on this device, so it is waiting for you when you go Premium.
          </p>
          <LookPicker look={look} tid="dj" />
          <div className="mt-3"><Sample look={look} /></div>
        </div>
      )}

      {ready && premium && !loaded && <p className="text-zinc-500 mt-6">Loading...</p>}

      {ready && premium && loaded && <div className="mt-4"><LookPicker look={look} tid="dj" /></div>}

      {ready && premium && loaded && (
        <div className="dj-console" style={look.vars} data-testid="dj-console" data-look={look.look}>
          {!booth && <PairCard onPaired={paired} />}

          {booth && (
            <>
              <div className="flex items-center justify-between" data-testid="dj-status">
                <span className={`text-sm flex items-center gap-2 ${booth.screen_live ? "dj-ok" : "dj-warn"}`}>
                  <span className="w-2.5 h-2.5 rounded-full bg-current" />
                  {booth.screen_live ? `Screen connected (${booth.code})` : "Waiting for the screen..."}
                </span>
                <button type="button" onClick={endBooth} data-testid="dj-end" className="dj-sw is-flat text-sm px-3 py-1.5 rounded-full">End booth</button>
              </div>
              {!wide && booth.decks.length > 2 && (
                <div className="grid grid-cols-2 gap-2 mt-3" data-testid="dj-pairs">
                  {[0, 1].map((pr) => {
                    const live = booth.decks.slice(pr * 2, pr * 2 + 2).filter((d) => d.play && !(d.rep && d.rep.e)).length;
                    return (
                      <button key={pr} type="button" onClick={() => setPair(pr)} data-testid={`dj-pair-${pr}`} aria-pressed={pair === pr}
                        className="dj-sw px-3 py-2 rounded-full text-sm">
                        Decks {pr * 2 + 1} &amp; {pr * 2 + 2}{live && pair !== pr ? ` · ${live} playing` : ""}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid="dj-decks">
                {booth.decks.map((d, i) => (
                  <div key={i} className={wide || Math.floor(i / 2) === pair ? "" : "hidden"}>
                    <Deck i={i} deck={d} send={send} holder={booth.party && booth.party.seats && booth.party.seats[i] ? booth.party.seats[i].name : ""} />
                  </div>
                ))}
              </div>
              <div className="max-w-3xl mx-auto">
                <Mixer booth={booth} send={send} wide={wide} pair={pair} />
                <PartyCard booth={booth} onBooth={(b) => setBooth((prev) => newer(prev, b))} />
                <Library booth={booth} send={send} decksShown={booth.decks.map((_, i) => i).filter((i) => wide || Math.floor(i / 2) === pair)} />
              </div>
            </>
          )}
        </div>
      )}

      {manual && <ManualBox premium={premium} user={user} onClose={closeManual} look={look} />}
    </div>
  );
}
